/* RESPONSIBLE TEAM: team-data-foundations */
import { attr, belongsTo, hasMany } from '@ember-data/model';
import type InsertableActionField from './insertable-action-field';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { isPresent } from '@ember/utils';
import type SuggestedQuestion from 'embercom/models/workflow-connector/suggested-question';
import { BaseAction } from 'embercom/models/workflow-connector/base-action';
import type ActionOutputParameterDescriptor from './action-output-parameter-descriptor';

export default class InsertableAction extends BaseAction {
  @attr('string') declare name: string;
  @attr('string') declare description: string;
  @attr('string') declare request_attribute_identifiers: string;
  @attr('string') declare response_attribute_identifiers: string;
  @attr('string') declare created_by: string;
  @attr('string') declare appPackageCode: string;
  @attr('boolean') declare isTemplate: boolean;
  @attr('string') declare url: string;
  @fragmentArray('workflow-connector/insertable-action-field')
  declare requestParameters: Array<InsertableActionField>;
  @fragmentArray('workflow-connector/insertable-action-field')
  declare responseFields: Array<InsertableActionField>;

  @attr('string') declare httpMethod: string;
  @attr('boolean') declare finEnabled: boolean;
  @attr('string') declare state: string;
  @attr('string') declare iconUrl: string;
  @attr('date') declare createdAt: Date;
  @attr('date') declare updatedAt: Date;
  @attr('number') declare createdByAdminId: number;
  @attr('number') declare updatedByAdminId: number;
  @attr('boolean', { defaultValue: false }) declare isRecommendedAction: boolean;
  @attr('string') declare tokenId: string | null;
  @attr('array', { defaultValue: () => [] }) declare tokenIds: (string | null)[];
  @attr('number') declare conversationUsage: number;
  @attr('boolean') declare otpAuthEnabled: boolean;
  @attr('number') declare sentCount: number;
  @attr('number') declare resolvedCount: number;
  @attr('string') declare exampleBody: string;
  @fragmentArray('workflow-connector/suggested-question')
  declare suggestedQuestions: SuggestedQuestion[];
  @fragment('workflow-connector/action-output-parameter-descriptor')
  declare actionOutputParameterDescriptor: ActionOutputParameterDescriptor;

  @hasMany('workflow-connector/insertable-action', { async: false })
  declare children: InsertableAction[];
  @belongsTo('workflow-connector/insertable-action', { async: false, inverse: 'children' })
  declare parent: InsertableAction;

  // Custom object inputs come with this format: https://github.com/intercom/intercom/blob/055b02f2c89cbb4a1fa81d2ec720924e794264d7/app/lib/custom_objects/commands/related_objects_context.rb#L55
  // For example: conversation.related_objects.<relationship id>.custom_objects.<custom object name>.<id>
  // All other attributes return their normal identifiers
  get requestAttributeIdentifiersArray() {
    return this.request_attribute_identifiers.split(',');
  }
  // Custom object outputs come with this format: custom_object_attribute.<id>
  // All other attributes return their normal identifiers
  get responseAttributeIdentifiersArray() {
    return this.response_attribute_identifiers.split(',');
  }

  get hasRequestParameters() {
    return isPresent(this.requestParameters) && this.requestParameters.length > 0;
  }

  get hasInputs() {
    let hasInput =
      isPresent(this.request_attribute_identifiers) &&
      this.request_attribute_identifiers.length > 0;

    if (this.usage === 'fin' || this.appService.app?.canUseConsolidatedActions) {
      hasInput = hasInput || this.hasRequestParameters;
    }

    return hasInput;
  }

  get hasOutputs() {
    let hasResponseFields = isPresent(this.responseFields) && this.responseFields.length > 0;
    let hasIdentifiers =
      isPresent(this.response_attribute_identifiers) &&
      this.response_attribute_identifiers.length > 0;

    if (this.appService.app?.canUseConsolidatedActions) {
      return hasResponseFields || hasIdentifiers;
    } else if (this.usage === 'fin') {
      return hasResponseFields;
    } else {
      return hasIdentifiers;
    }
  }
}
